// Zeiträume der Auswertungen – reine Regeln für Server UND Oberfläche. Alle Tage sind Geschäftstage
// ('YYYY-MM-DD', Europe/Berlin); ein Zeitraum schließt beide Grenzen ein.
import { addDays, addMonths, diffDays, endOfMonth, startOfMonth, startOfWeek, startOfYear, type IsoDate } from '@shared/time'

export interface Period {
  from: IsoDate
  to: IsoDate
}

/** Benannte Zeiträume – relativ zu „heute“. Damit kommt eine Frage wie „letzte Woche“ ohne Datumsrechnung aus. */
export const PERIOD_PRESETS = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'last7', 'last30', 'last90', 'thisYear'] as const
export type PeriodPreset = (typeof PERIOD_PRESETS)[number]

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  today: 'Heute',
  yesterday: 'Gestern',
  thisWeek: 'Diese Woche',
  lastWeek: 'Letzte Woche',
  thisMonth: 'Dieser Monat',
  lastMonth: 'Letzter Monat',
  last7: 'Letzte 7 Tage',
  last30: 'Letzte 30 Tage',
  last90: 'Letzte 90 Tage',
  thisYear: 'Dieses Jahr',
}

/** Zeitraum eines benannten Zeitraums. Laufende Zeiträume enden heute („diese Woche“ = Montag bis heute). */
export function presetPeriod(preset: PeriodPreset, today: IsoDate): Period {
  switch (preset) {
    case 'today':
      return { from: today, to: today }
    case 'yesterday':
      return { from: addDays(today, -1), to: addDays(today, -1) }
    case 'thisWeek':
      return { from: startOfWeek(today), to: today }
    case 'lastWeek': {
      const monday = addDays(startOfWeek(today), -7)
      return { from: monday, to: addDays(monday, 6) }
    }
    case 'thisMonth':
      return { from: startOfMonth(today), to: today }
    case 'lastMonth': {
      const first = addMonths(startOfMonth(today), -1)
      return { from: first, to: endOfMonth(first) }
    }
    case 'last7':
      return { from: addDays(today, -6), to: today }
    case 'last30':
      return { from: addDays(today, -29), to: today }
    case 'last90':
      return { from: addDays(today, -89), to: today }
    case 'thisYear':
      return { from: startOfYear(today), to: today }
  }
}

export const periodDays = (period: Period): number => diffDays(period.from, period.to) + 1

export const COMPARE_MODES = ['none', 'previous', 'year'] as const
export type CompareMode = (typeof COMPARE_MODES)[number]

export const COMPARE_LABELS: Record<CompareMode, string> = { none: 'Kein Vergleich', previous: 'Vorperiode', year: 'Vorjahr' }

const isWholeMonths = (period: Period): boolean => period.from === startOfMonth(period.from) && period.to === endOfMonth(period.to)

/**
 * Vergleichszeitraum:
 *   'previous'  der gleich lange Zeitraum unmittelbar davor – so trifft eine Woche wieder dieselben Wochentage.
 *               Ganze Kalendermonate vergleichen mit den ganzen Monaten davor (Februar gegen Januar, nicht 28 Tage).
 *   'year'      derselbe Zeitraum zwölf Monate früher.
 */
export function comparisonPeriod(period: Period, mode: CompareMode): Period | null {
  if (mode === 'none') return null
  if (mode === 'year') {
    return isWholeMonths(period)
      ? { from: addMonths(period.from, -12), to: endOfMonth(addMonths(startOfMonth(period.to), -12)) }
      : { from: addMonths(period.from, -12), to: addMonths(period.to, -12) }
  }
  if (isWholeMonths(period)) {
    const months = (Number(period.to.slice(0, 4)) - Number(period.from.slice(0, 4))) * 12 + Number(period.to.slice(5, 7)) - Number(period.from.slice(5, 7)) + 1
    const from = addMonths(period.from, -months)
    return { from, to: addDays(period.from, -1) }
  }
  const days = periodDays(period)
  return { from: addDays(period.from, -days), to: addDays(period.from, -1) }
}

/** Kurzform des Vergleichs für Kennzahlen: „ggü. Vorwoche“, „ggü. Vormonat“, „ggü. Vorjahr“. */
export function comparisonLabel(period: Period, mode: CompareMode): string {
  if (mode === 'year') return 'ggü. Vorjahr'
  const days = periodDays(period)
  if (days === 1) return 'ggü. Vortag'
  if (days === 7) return 'ggü. Vorwoche'
  if (isWholeMonths(period) && period.from.slice(0, 7) === period.to.slice(0, 7)) return 'ggü. Vormonat'
  return 'ggü. Vorperiode'
}

export const TIME_BUCKETS = ['day', 'week', 'month'] as const
export type TimeBucket = (typeof TIME_BUCKETS)[number]

/** Erster Tag des Abschnitts, in den der Tag fällt (Woche beginnt am Montag). */
export function bucketStart(date: IsoDate, bucket: TimeBucket): IsoDate {
  return bucket === 'day' ? date : bucket === 'week' ? startOfWeek(date) : startOfMonth(date)
}

/** Alle Abschnitte eines Zeitraums in zeitlicher Folge – auch die ohne Umsatz (eine Lücke ist eine Auskunft). */
export function bucketsOf(period: Period, bucket: TimeBucket): IsoDate[] {
  const result: IsoDate[] = []
  let cursor = bucketStart(period.from, bucket)
  while (cursor <= period.to) {
    result.push(cursor)
    cursor = bucket === 'day' ? addDays(cursor, 1) : bucket === 'week' ? addDays(cursor, 7) : addMonths(cursor, 1)
  }
  return result
}

/** Passende Auflösung für die Zeitachse: bis fünf Wochen je Tag, bis ein halbes Jahr je Woche, sonst je Monat. */
export function defaultBucket(period: Period): TimeBucket {
  const days = periodDays(period)
  return days <= 35 ? 'day' : days <= 190 ? 'week' : 'month'
}

/** Veränderung in Basispunkten (620 = +6,2 %); null ohne Vergleichswert – durch null teilt niemand. */
export function changeBp(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined || previous === 0) return null
  return Math.round(((current - previous) * 10_000) / Math.abs(previous))
}

/** Anteil in Basispunkten; 0, wenn es kein Ganzes gibt. */
export const shareBp = (part: number, whole: number): number => (whole === 0 ? 0 : Math.round((part * 10_000) / whole))
